import { isString } from "util";

import { hasOwnProperty } from "@glideapps/ts-necessities";

import type { BaseRowIndex } from "./firebase-function-types";

export enum PaymentProcessingResultKind {
    Success = "ok",
    // This is extremely processor-dependent on what this means.
    // Stripe might ask the client to go hook into the SDK again,
    // e.g. for 3-D Secure.
    ClientActionRequired = "client-action-required",
    ProcessorFailure = "failure",
    ProductNotFound = "not-found",
    ProductNotFoundV2 = "not-found-v2",
    ClientError = "client-error",
    StoreClosed = "store-closed",
}

export interface PaymentSuccessResult {
    kind: PaymentProcessingResultKind.Success;
    receiptEmail: string | undefined;
    processorLink: string | undefined;
}

export function isPaymentSuccessResult(x: unknown): x is PaymentSuccessResult {
    return (
        isPaymentProcessingResult(x) &&
        (!hasOwnProperty(x, "receiptEmail") || isString(x.receiptEmail)) &&
        (!hasOwnProperty(x, "processorLink") || isString(x.processorLink))
    );
}

export enum PaymentProcessingFailureKind {
    ConnectionError = "connection-error",
    InternalError = "internal-error",
    AuthenticationError = "authentication-error",
    CardError = "card-error",
    CardDeclinedError = "card-declined-error",
    InsufficientCharge = "insufficient-charge",
}

export interface PaymentClientActionRequiredResult {
    kind: PaymentProcessingResultKind.ClientActionRequired;
    // FIXME: This is extremely Stripe-specific. Make it more general.
    processorPayload: {
        paymentIntentID: string;
        intentClientSecret: string;
        tdsRedirectURL: string;
        purchaseID: string;
    };
}

export function isPaymentClientActionRequiredResult(x: unknown): x is PaymentClientActionRequiredResult {
    return isPaymentProcessingResult(x) && x.kind === PaymentProcessingResultKind.ClientActionRequired;
}

interface PaymentFailureResult {
    kind: PaymentProcessingResultKind.ProcessorFailure;
    message: string;
    type: PaymentProcessingFailureKind;
}

export function isPaymentFailureResult(x: unknown): x is PaymentFailureResult {
    return (
        isPaymentProcessingResult(x) &&
        x.kind === PaymentProcessingResultKind.ProcessorFailure &&
        hasOwnProperty(x, "message") &&
        hasOwnProperty(x, "type")
    );
}

export function isPaymentFailureCardErrorResult(x: unknown): x is PaymentFailureResult {
    return isPaymentFailureResult(x) && x.type === PaymentProcessingFailureKind.CardError;
}

export function isPaymentFailureCardDeclinedErrorResult(x: unknown): x is PaymentFailureResult {
    return isPaymentFailureResult(x) && x.type === PaymentProcessingFailureKind.CardDeclinedError;
}

export function isPaymentFailureInsufficientCharge(x: unknown): x is PaymentFailureResult {
    return isPaymentFailureResult(x) && x.type === PaymentProcessingFailureKind.InsufficientCharge;
}

interface PaymentProductNotFoundResult {
    kind: PaymentProcessingResultKind.ProductNotFound;
}

export type MissingProducts = { sku: string | number; rowIndex?: BaseRowIndex }[];

interface PaymentProductNotFoundV2Result {
    kind: PaymentProcessingResultKind.ProductNotFoundV2;
    missing: MissingProducts;
}

interface PaymentStoreClosedResult {
    kind: PaymentProcessingResultKind.StoreClosed;
}

interface PaymentClientErrorResult {
    kind: PaymentProcessingResultKind.ClientError;
}

export type PaymentProcessingResult =
    | PaymentSuccessResult
    | PaymentClientActionRequiredResult
    | PaymentFailureResult
    | PaymentProductNotFoundResult
    | PaymentProductNotFoundV2Result
    | PaymentClientErrorResult
    | PaymentStoreClosedResult;

export function isPaymentProcessingResult(x: unknown): x is PaymentProcessingResult {
    return typeof x === "object" && hasOwnProperty(x, "kind") && typeof x.kind === "string";
}
